import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { placesPageQueryOptions } from "common/hooks/places/placesQueryOptions";
import type { PlacesCityParams } from "common/types/places";
import { PlacesListing } from "@/components/Places/PlacesListing";
import { PlacesUnavailable } from "@/components/Places/PlacesUnavailable";
import { buildPlacesPageHead, parsePlacesSearch } from "@/lib/placesPage";
import { loadPlacesPage } from "@/lib/placesPageLoader";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

// /places/<city>/<type> or /places/<city>/<area>; the API decides which, a type slug first.
export const Route = createFileRoute("/places/$citySlug_/$segment")({
  ssr: true,
  component: PlacesSegment,
  errorComponent: PlacesUnavailable,
  validateSearch: (search: Record<string, unknown>): PlacesCityParams => parsePlacesSearch(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, params, deps: { search } }) =>
    loadPlacesPage(context.queryClient, params.citySlug, [params.segment], search),
  head: ({ loaderData, params }) =>
    buildPlacesPageHead(
      loaderData?.page,
      // Without data an outage page can't tell a type from an area; the place form reads right for both.
      loaderData?.page.type
        ? { citySlug: params.citySlug, typeSlug: params.segment }
        : { citySlug: params.citySlug, areaSlug: params.segment },
      loaderData?.search ?? {},
      loaderData?.canonicalOrigin ?? "",
      isProduction,
    ),
});

function PlacesSegment() {
  const { citySlug, segment } = Route.useParams();
  const search = Route.useSearch();
  const { data } = useQuery(placesPageQueryOptions(citySlug, [segment], search));

  if (!data?.data) {
    return null;
  }

  return (
    <PlacesListing
      data={data.data}
      citySlug={citySlug}
      areaSlug={data.data.area ? segment : undefined}
      typeSlug={data.data.type ? segment : undefined}
      search={search}
    />
  );
}
